import { RouteRecordRaw } from 'vue-router';
import { AppCoreEnums } from 'src/core/shared/enums';
import { ResolveParams, IRouteMeta } from 'src/core/models/routing';
import { AppPermissions } from 'src/shared/permissions';
import { ROUTE_PATH_PROPERTIES } from 'src/shared/constants';
import { useUsersStore } from '@/stores/users/store';
import { User } from '@/stores/users/models';
import useCoreUtils from '@/core/composable/use-core-utils';

const { propertyOf } = useCoreUtils();

const ROUTE_USERS_ROOT = 'users-root';
const ROUTE_USERS = 'users';
const ROUTE_USER_DETAILS = 'user-details';
const ROUTE_USER_OVERVIEW = 'user-overview';
const ROUTE_USER_PROPERTIES = 'user-properties';

const usersRoutes: RouteRecordRaw = {
  name: ROUTE_USERS_ROOT,
  path: ROUTE_USERS,
  component: () => import('@/core/containers/empty-container.vue'),
  meta: <IRouteMeta>{
    containerType: AppCoreEnums.ContainerType.EmptyContainer,
    permissions: { readPermission: AppPermissions.UsersRead },
  },
  redirect: { name: ROUTE_USERS },
  children: [
    {
      name: ROUTE_USERS,
      path: 'list',
      component: () => import('@/pages/users/users-page.vue'),
      meta: <IRouteMeta>{
        collapsibleTableTitleField: propertyOf<User>('fullName'),
        resolve: async (resolveParams: ResolveParams) => {
          const { useUsersStore } = await import('@/stores/users/store');
          return useUsersStore().loadUsers(resolveParams);
        },
        permissions: {
          readPermission: AppPermissions.UsersRead,
          writePermission: AppPermissions.UsersWrite,
          deletePermission: AppPermissions.UsersDelete,
        },
        containerType: AppCoreEnums.ContainerType.CollapsibleTableContainer,
      },
      children: [
        {
          name: ROUTE_USER_DETAILS,
          path: ':id',
          component: () => import('@/core/pages/app-tabs-container-page.vue'),
          meta: <IRouteMeta>{
            containerType: AppCoreEnums.ContainerType.TabsContainer,
            resolve: async (resolveParams: ResolveParams) => {
              const { useUsersStore } = await import('@/stores/users/store');
              return useUsersStore().loadUserById(resolveParams);
            },
            permissions: {
              readPermission: AppPermissions.UsersRead,
              writePermission: AppPermissions.UsersWrite,
            },
            tabs: [
              {
                label: 'core.overview',
                to: ROUTE_USER_OVERVIEW,
                icon: 'hugeicons:profile-02',
              },
              {
                label: 'core.properties',
                to: ROUTE_USER_PROPERTIES,
                icon: 'hugeicons:pencil-edit-02',
              },
            ],
          },
          redirect: { name: ROUTE_USER_OVERVIEW },
          children: [
            {
              name: ROUTE_USER_OVERVIEW,
              path: 'overview',
              component: () => import('@/core/pages/app-form-container-page.vue'),
              meta: <IRouteMeta>{
                containerType: AppCoreEnums.ContainerType.FormsContainer,
                formComponent: () => import('@/forms/users/user-overview-form.vue'),
                formModel: () => useUsersStore().get('userOverview'),
                resolve: async (resolveParams: ResolveParams) => {
                  if (!resolveParams.useStoreData) return Promise.resolve();
                  const { useUsersStore } = await import('@/stores/users/store');
                  return useUsersStore().loadUserById(resolveParams);
                },
                permissions: {
                  readPermission: AppPermissions.UsersRead,
                },
              },
            },
            {
              name: ROUTE_USER_PROPERTIES,
              path: ROUTE_PATH_PROPERTIES,
              component: () => import('@/core/pages/app-form-container-page.vue'),
              meta: <IRouteMeta>{
                containerType: AppCoreEnums.ContainerType.FormsContainer,
                formComponent: () => import('@/forms/users/user-properties-form.vue'),
                formModel: () => useUsersStore().get('userProperties'),
                formSave: () => useUsersStore().saveUser,
                resolve: async (resolveParams: ResolveParams) => {
                  if (!resolveParams.useStoreData) return Promise.resolve();
                  const { useUsersStore } = await import('@/stores/users/store');
                  return useUsersStore().loadUserById(resolveParams);
                },
                permissions: {
                  readPermission: AppPermissions.UsersRead,
                  writePermission: AppPermissions.UsersWrite,
                },
              },
            },
          ],
        },
      ],
    },
  ],
};

export { usersRoutes, ROUTE_USERS, ROUTE_USERS_ROOT, ROUTE_USER_DETAILS, ROUTE_USER_OVERVIEW, ROUTE_USER_PROPERTIES };
